import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback } from "./ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { AlertCircle } from "lucide-react";
import { HOME_ROUTE } from "@/utils/const";

// Types
type OSInfo = {
  os: string;
  osVersion: string;
};

type DebugInfo = {
  ip: string;
  browser: string;
  language: string;
  timezone: string;
  currentTime: string;
  javascript: string;
  screenResolution: string;
  os: string;
  osVersion: string;
};

// Constants
const OS_PATTERNS = [
  {
    pattern: /Windows NT (\d+\.\d+)/,
    os: "Windows",
    version: (match: RegExpMatchArray) => match[1],
  },
  {
    pattern: /Mac OS X (\d+[._]\d+)/,
    os: "macOS",
    version: (match: RegExpMatchArray) => match[1].replace("_", "."),
  },
  { pattern: /Linux\s+([^\s]+)/, os: "Linux", version: (match: RegExpMatchArray) => match[1] },
  { pattern: /Android\s+([^\s;]+)/, os: "Android", version: (match: RegExpMatchArray) => match[1] },
  {
    pattern: /OS\s+(\d+_\d+)/,
    os: "iOS",
    version: (match: RegExpMatchArray) => match[1].replace("_", "."),
  },
] as const;

const DEFAULT_DEBUG_INFO: DebugInfo = {
  ip: "Не удалось определить",
  browser: navigator.userAgent,
  language: navigator.language,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  currentTime: new Date().toLocaleString("ru-RU", { timeZone: "Europe/Moscow" }) + " MSK",
  javascript: "Да",
  screenResolution: `${window.innerWidth}x${window.innerHeight}`,
  os: "Неизвестно",
  osVersion: "",
};

// Utility functions
const getOSInfo = (userAgent: string): OSInfo => {
  for (const { pattern, os, version } of OS_PATTERNS) {
    const match = userAgent.match(pattern);
    if (match) return { os, osVersion: version(match) };
  }
  return { os: "Неизвестно", osVersion: "" };
};

const fetchDebugInfo = async (): Promise<DebugInfo> => {
  try {
    const response = await fetch("https://api.ipify.org?format=json");
    const { ip } = await response.json();
    const userAgent = navigator.userAgent;
    const { os, osVersion } = getOSInfo(userAgent);

    return {
      ip,
      browser: userAgent,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      currentTime: new Date().toLocaleString("ru-RU", { timeZone: "Europe/Moscow" }) + " MSK",
      javascript: "Да",
      screenResolution: `${window.innerWidth}x${window.innerHeight}`,
      os,
      osVersion,
    };
  } catch {
    return DEFAULT_DEBUG_INFO;
  }
};

// Components
const DebugInfoRow = ({ label, value }: { label: string; value: string }) => (
  <div className="grid grid-cols-[140px,1fr] gap-2">
    <div className="text-sm font-medium text-gray-500">{label}:</div>
    <div className="break-all text-sm text-gray-900">{value}</div>
  </div>
);

const DebugInfoDisplay = ({ info }: { info: DebugInfo }) => (
  <div className="space-y-3 rounded-lg border bg-gray-50 p-4">
    {Object.entries(info).map(([key, value]) => (
      <DebugInfoRow
        key={key}
        label={
          key === "os"
            ? "Операционная система"
            : key === "osVersion"
              ? "Версия"
              : key.charAt(0).toUpperCase() + key.slice(1)
        }
        value={key === "os" ? `${info.os}${info.osVersion ? ` ${info.osVersion}` : ""}` : value}
      />
    ))}
  </div>
);

const ErrorHeader = () => (
  <>
    <div className="mb-6 flex items-center justify-center">
      <Avatar className="h-12 w-12 bg-destructive/10">
        <AvatarFallback className="bg-transparent">
          <AlertCircle className="h-6 w-6 text-destructive" />
        </AvatarFallback>
      </Avatar>
    </div>
    <h1 className="mb-2 text-center text-xl font-semibold">
      К сожалению, произошла ошибка в приложении
    </h1>
    <p className="mb-6 text-center text-muted-foreground">
      Мы уже работаем над устранением проблемы. Пожалуйста, попробуйте позже.
    </p>
  </>
);

const DebugDialog = ({
  isOpen,
  onClose,
  debugInfo,
  onCopy,
  isCopied,
}: {
  isOpen: boolean;
  onClose: () => void;
  debugInfo: DebugInfo | null;
  onCopy: () => void;
  isCopied: boolean;
}) => (
  <Dialog open={isOpen} onOpenChange={onClose}>
    <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-[500px]">
      <DialogHeader>
        <DialogTitle>Отладочная информация</DialogTitle>
        <DialogDescription>Эта информация может помочь в диагностике проблемы</DialogDescription>
      </DialogHeader>
      <div className="flex-1 space-y-4 overflow-y-auto py-4">
        {debugInfo && <DebugInfoDisplay info={debugInfo} />}
      </div>
      <DialogFooter className="mt-4 flex-col sm:flex-row sm:justify-end">
        <Button
          onClick={onCopy}
          variant="secondary"
          className="mb-2 w-full min-w-[100px] sm:mb-0 sm:mr-2 sm:w-auto"
        >
          {isCopied ? "Скопировано!" : "Копировать"}
        </Button>
        <Button onClick={onClose} className="w-full sm:w-auto">
          Закрыть
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export function FallbackComponent() {
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [copied, setCopied] = useState(false);
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null);

  useEffect(() => {
    fetchDebugInfo().then(setDebugInfo);
  }, []);

  const handleCopyDebugInfo = async () => {
    if (!debugInfo) return;

    const debugText = Object.entries(debugInfo)
      .map(([key, value]) => `${key}: ${value}`)
      .join("\n");

    try {
      await navigator.clipboard.writeText(debugText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Failed to copy to clipboard
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="mx-auto max-w-[600px] px-4">
        <div className="rounded-lg bg-white p-6">
          <ErrorHeader />
          <div className="flex flex-col justify-center gap-2">
            <Button
              onClick={() => setShowDebugInfo(true)}
              variant="outline"
              className="border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              Детальная информация
            </Button>
            <Button
              onClick={() => (window.location.href = HOME_ROUTE)}
              variant="link"
              className="ml-2 border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              На главную
            </Button>
          </div>
        </div>

        <DebugDialog
          isOpen={showDebugInfo}
          onClose={() => setShowDebugInfo(false)}
          debugInfo={debugInfo}
          onCopy={handleCopyDebugInfo}
          isCopied={copied}
        />
      </div>
    </div>
  );
}
